import {
  ChartBarIcon,
  ExclamationTriangleIcon,
  ListBulletIcon,
  TrophyIcon,
} from '@heroicons/react/24/outline';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/solid';
import {
  Accordion,
  Badge,
  Card,
  Flex,
  Grid,
  Group,
  MantineProvider,
  Paper,
  Select,
  SimpleGrid,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import React, { useState } from 'react';
import {
  ProgressReportDataType,
  ThemeTopic,
} from 'src/interfaces/ProgressReport';
import { NOT_AVAILABLE_PLACE_HOLDER } from 'src/utils/helpers';

// ---------------- Metric Card ----------------
const MetricCard: React.FC<{
  title: string;
  value?: string | number;
  icon: React.ReactNode;
  color: string;
  content?: React.ReactNode;
}> = ({ title, value, icon, color, content }) => (
  <Card
    shadow="sm"
    padding="lg"
    radius="md"
    style={{
      backgroundColor: '#fff',
      border: '1px solid #e0e0e0',
      textAlign: 'center',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      height: '100%',
    }}
  >
    <div
      style={{
        backgroundColor: color,
        padding: '8px',
        borderRadius: '8px',
        marginBottom: '10px',
        display: 'flex',
        justifyContent: 'center',
      }}
    >
      <div style={{ width: '20px', height: '20px', color: '#1e40af' }}>
        {icon}
      </div>
    </div>
    <Text size="sm" color="#757575" style={{ marginBottom: '5px' }}>
      {title}
    </Text>
    {value !== undefined && value !== null && (
      <Text size="xl" fw={700} mb={content ? 'sm' : 0}>
        {value}
      </Text>
    )}
    {content && (
      <div style={{ marginTop: '8px', width: '100%' }}>{content}</div>
    )}
  </Card>
);

// ---------------- Badge List Card Content ----------------
const BadgeList: React.FC<{
  items: string[];
  color?: string;
}> = ({ items, color = 'blue' }) => (
  <Flex gap="xs" wrap="wrap" align="flex-start">
    {items.map((item, i) => (
      <Badge
        key={i}
        variant="light"
        color={color}
        size="sm"
        style={{
          whiteSpace: 'normal',
          wordBreak: 'break-word',
          overflowWrap: 'anywhere',
          lineHeight: 1.2,
          textAlign: 'left',
          padding: '6px 10px',
          maxWidth: '100%',
          height: 'auto',
        }}
      >
        {item}
      </Badge>
    ))}
  </Flex>
);

// ---------------- Mastery Accordion (uses levels now) ----------------
const getAccuracyColor = (accuracy: number | string) => {
  if (typeof accuracy === 'string') return 'gray';
  if (accuracy > 75) return 'green';
  if (accuracy >= 50) return 'yellow';
  return 'red';
};

const MasteryList: React.FC<{
  items: { levels: { [levelName: string]: ThemeTopic[] } };
}> = ({ items }) => {
  const levelNames = Object.keys(items.levels || {});
  const [selectedLevel, setSelectedLevel] = useState<string | null>(
    levelNames[0] || null
  );
  const [openMiscon, setOpenMiscon] = useState<{ [key: string]: boolean }>({});

  if (!selectedLevel) return null;

  return (
    <Paper
      shadow="sm"
      p="lg"
      radius="md"
      withBorder
      style={{ backgroundColor: '#fff' }}
    >
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <Title order={3} c="#333">
          Mastery by Topic/Concept
        </Title>
        <Select
          placeholder="Select Level"
          data={levelNames}
          onChange={(value) => setSelectedLevel(value)}
          value={selectedLevel}
        />
      </div>
      <Accordion variant="separated">
        {(items.levels[selectedLevel] || []).map((item, index) => (
          <Accordion.Item key={index} value={item.topic}>
            <Accordion.Control>
              <Group className="flex items-center">
                <Text fw={500}>{item.topic.charAt(0).toUpperCase() + item.topic.slice(1)}</Text>
                {item.level && item.level.toLowerCase() == 'all' && (
                  <Badge color="gray" variant="light">
                    Level: {item.level}
                  </Badge>
                )}
                <Badge
                  color={getAccuracyColor(item.metrics.accuracyRate)}
                  variant="filled"
                >
                  {item.metrics.accuracyRate}%
                </Badge>
              </Group>
            </Accordion.Control>
            <Accordion.Panel>
              <SimpleGrid cols={2} spacing="md" breakpoints={[{ minWidth: 'md', cols: 3 }]}>
                <div>
                  <Text size="sm" fw={500}>
                    Accuracy Rate
                  </Text>
                  <Text size="xs" color="dimmed">
                    {item.metrics.accuracyRate}%
                  </Text>
                </div>
                <div>
                  <Text size="sm" fw={500}>
                    Attempts
                  </Text>
                  <Badge color="blue">{item.metrics.attempts}</Badge>
                </div>
                <div>
                  <Text size="sm" fw={500}>
                    Correct vs Incorrect
                  </Text>
                  <Text size="xs" color="dimmed">
                    {(() => {
                      const correctVal =
                        Number(
                          typeof item.metrics.correct !== 'undefined'
                            ? item.metrics.correct
                            : (() => {
                              const acc =
                                typeof item.metrics.accuracyRate === 'number'
                                  ? item.metrics.accuracyRate
                                  : parseFloat(
                                    String(item.metrics.accuracyRate)
                                      .toString()
                                      .replace('%', '')
                                  ) || 0;
                              const at = Number(item.metrics.attempts) || 0;
                              return Math.round((acc / 100) * at);
                            })()
                        ) || 0;
                      const incorrectVal =
                        Number(
                          typeof item.metrics.incorrect !== 'undefined'
                            ? item.metrics.incorrect
                            : (() => {
                              const at = Number(item.metrics.attempts) || 0;
                              return at - correctVal;
                            })()
                        ) || 0;
                      return `${correctVal} vs ${incorrectVal}`;
                    })()}
                  </Text>
                </div>
                <div>
                  <Text size="sm" fw={500}>
                    Mastery Level
                  </Text>
                  <Badge color="green">{item.metrics.bloomLevel}</Badge>
                </div>
                <div>
                  <Text size="sm" fw={500}>
                    Hints Requested
                  </Text>
                  <Badge color="orange">{item.metrics.hintsRequested}</Badge>
                </div>
                {item.metrics.misconceptions == 0 && (
                  <div>
                    <Text size="sm" fw={500}>
                      Misconceptions
                    </Text>
                    <Badge color="orange">0</Badge>
                  </div>
                )}
              </SimpleGrid>
              {Array.isArray(item.metrics.misconceptions) &&
                item.metrics.misconceptions.length > 0 && (
                  <div className="mt-3 bg-gray-100 rounded-md p-2">
                    <div
                      className="flex items-center justify-between cursor-pointer"
                      onClick={() =>
                        setOpenMiscon((prev) => ({
                          ...prev,
                          [item.topic]: !prev[item.topic],
                        }))
                      }
                    >
                      <Text size="sm" fw={500}>
                        Misconceptions
                      </Text>
                      {openMiscon[item.topic] ? (
                        <ChevronUpIcon className="w-3 h-3" />
                      ) : (
                        <ChevronDownIcon className="w-3 h-3" />
                      )}
                    </div>
                    <div className="px-2">
                      {openMiscon[item.topic] &&
                        Array.isArray(item.metrics.misconceptions) && (
                          <Stack spacing="xs" mt="sm">
                            {item.metrics.misconceptions.map((m, idx) => (
                              <Text
                                key={idx}
                                className="text-sm"
                                style={{
                                  display: 'block',
                                  whiteSpace: 'normal',
                                  wordBreak: 'break-word',
                                  overflowWrap: 'anywhere',
                                  textAlign: 'left',
                                  lineHeight: 1.4,
                                }}
                              >
                                {`${idx + 1}. ${m}`} {/* prepend number */}
                              </Text>
                            ))}
                          </Stack>
                        )}
                    </div>
                  </div>
                )}
            </Accordion.Panel>
          </Accordion.Item>
        ))}
      </Accordion>
    </Paper>
  );
};

// ---------------- Main Report View ----------------
export const ProgressReportView = ({
  data,
}: {
  data?: ProgressReportDataType | null;
}) => {
  if (!data) return <p>No data available</p>;

  // ✅ Type-safe array extraction
  const safeArray = (val: string[] | string | undefined): string[] =>
    Array.isArray(val) ? val : [];

  return (
    <MantineProvider theme={{ colorScheme: 'light' }}>
      <div style={{ height: '100vh', width: '100%', overflow: 'auto', backgroundColor: '#f5f5f5' }}>
        <Grid
          gutter="md"
          style={{
            padding: '20px',
            margin: 0,
            maxWidth: '100%',
          }}
        >
          {/* Row 1 */}
          <Grid.Col xs={12} sm={6} lg={3}>
            <MetricCard
              title="Total Questions Attempted"
              value={data.totalQuestionsAttempted}
              icon={<ChartBarIcon />}
              color="#e0e7ff"
            />
          </Grid.Col>

          <Grid.Col xs={12} sm={6} lg={3}>
            <MetricCard
              title="Milestones Achieved"
              icon={<TrophyIcon />}
              color="#fee2e2"
              content={
                <div style={{ width: '100%' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid #e0e0e0' }}>
                        <th style={{ padding: '6px 8px', textAlign: 'left', fontWeight: 600, color: '#333' }}>Level</th>
                        <th style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 600, color: '#333' }}>Attempted</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(data.levels || {})
                        .filter(([levelName]) => levelName !== 'All')
                        .map(([levelName, topics]) => {
                          const totalTopics = topics.length;
                          const attemptedTopics = topics.filter(
                            (topic) => {
                              const attempts = topic.metrics.attempts;
                              return typeof attempts === 'number' ? attempts > 0 : false;
                            }
                          ).length;
                          return (
                            <tr key={levelName} style={{ borderBottom: '1px solid #f5f5f5' }}>
                              <td style={{ padding: '6px 8px', textAlign: 'left', color: '#555' }}>{levelName}</td>
                              <td style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 500, color: '#1e40af' }}>
                                {attemptedTopics} out of {totalTopics} topics
                              </td>
                            </tr>
                          );
                        })}
                    </tbody>
                  </table>
                </div>
              }
            />
          </Grid.Col>

          {/* Row 2 */}
          <Grid.Col xs={12} sm={6} lg={3}>
            <MetricCard
              title="Challenging Topics"
              icon={<ExclamationTriangleIcon />}
              color="#f3e8ff"
              content={
                safeArray(data.challengingTopics).length > 0 ? (
                  <BadgeList
                    items={safeArray(data.challengingTopics)}
                    color="red"
                  />
                ) : (
                  <Text size="sm" color="dimmed">
                    {NOT_AVAILABLE_PLACE_HOLDER}
                  </Text>
                )
              }
            />
          </Grid.Col>

          {/* Row 3 */}
          <Grid.Col xs={12} sm={6} lg={3}>
            <MetricCard
              title="Top Performing Topics"
              icon={<ChartBarIcon />}
              color="#faf5ff"
              content={
                safeArray(data.topPerformingTopics).length > 0 ? (
                  <BadgeList items={safeArray(data.topPerformingTopics)} />
                ) : (
                  <Text size="sm" color="dimmed">
                    {NOT_AVAILABLE_PLACE_HOLDER}
                  </Text>
                )
              }
            />
          </Grid.Col>
          <Grid.Col xs={12}>
            <MetricCard
              title="Recommended Focus Areas"
              icon={<ListBulletIcon />}
              color="#fff7ed"
              content={
                safeArray(data.recommendedFocusAreas).length > 0 ? (
                  <BadgeList
                    items={safeArray(data.recommendedFocusAreas)}
                    color="blue"
                  />
                ) : (
                  <Text size="sm" color="dimmed">
                    {NOT_AVAILABLE_PLACE_HOLDER}
                  </Text>
                )
              }
            />
          </Grid.Col>

          {/* Mastery by Topic */}
          <Grid.Col span={12}>
            <MasteryList items={{ levels: data.levels }} />
          </Grid.Col>
        </Grid>
      </div>
    </MantineProvider>
  );
};
